
import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    services: [] as string[],
    budget: '',
    timeline: '',
    contactMethod: '',
    projectDetails: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleCheckboxChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(value)
        ? prev.services.filter((s) => s !== value)
        : [...prev.services, value],
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);

      const response = await fetch('https://readdy.ai/api/form/d6f873n1jbcgr9kd6q70', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          fullName: '',
          businessName: '',
          email: '',
          phone: '',
          website: '',
          industry: '',
          services: [],
          budget: '',
          timeline: '',
          contactMethod: '',
          projectDetails: '',
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <i className="ri-check-line text-3xl text-primary"></i>
        </div>
        <h3 className="font-sora font-bold text-2xl text-charcoal mb-3">
          Thanks — I received your request.
        </h3>
        <p className="font-inter text-base text-gray-600 mb-8">
          I'll reach out within 24 hours with next steps.
        </p>
        <button
          onClick={() => setSubmitSuccess(false)}
          className="font-inter font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
      <h2 className="font-sora font-bold text-3xl text-charcoal mb-8">
        Start Your Project
      </h2>

      <form id="book-call-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
        {/* Full Name + Business Name */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="fullName" className="block font-inter text-sm font-medium text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              required
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="Your name"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
          </div>
          <div>
            <label htmlFor="businessName" className="block font-inter text-sm font-medium text-gray-700 mb-2">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              required
              value={formData.businessName}
              onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
              placeholder="Business name"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
          </div>
        </div>

        {/* Email + Phone */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block font-inter text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="you@business.com"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block font-inter text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(###) ###-####"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
          </div>
        </div>

        {/* Current Website (optional) */}
        <div>
          <label htmlFor="website" className="block font-inter text-sm font-medium text-gray-700 mb-2">
            Current Website <span className="text-gray-400 font-normal">(optional, if you don't have one, leave blank)</span>
          </label>
          <input
            type="url"
            id="website"
            name="website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="https://"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
        </div>

        {/* Industry */}
        <div>
          <label htmlFor="industry" className="block font-inter text-sm font-medium text-gray-700 mb-2">
            Industry <span className="text-red-500">*</span>
          </label>
          <select
            id="industry"
            name="industry"
            required
            value={formData.industry}
            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors cursor-pointer"
          >
            <option value="">Select an industry</option>
            <option value="Gym / Fitness">Gym / Fitness</option>
            <option value="Restaurant / Food">Restaurant / Food</option>
            <option value="Salon / Beauty">Salon / Beauty</option>
            <option value="Contractor / Home Services">Contractor / Home Services</option>
            <option value="Auto / Repair">Auto / Repair</option>
            <option value="Medical / Dental">Medical / Dental</option>
            <option value="Retail">Retail</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* What do you need help with? */}
        <div>
          <label className="block font-inter text-sm font-medium text-gray-700 mb-3">
            What do you need help with? <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            {[
              'New Website',
              'Website Redesign',
              'Local SEO / Google Rankings',
              'Website Maintenance',
              'Landing Page',
              'Not Sure Yet',
            ].map((service) => (
              <label key={service} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="services"
                  value={service}
                  checked={formData.services.includes(service)}
                  onChange={() => handleCheckboxChange(service)}
                  className="w-5 h-5 border-2 border-gray-300 rounded text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                />
                <span className="font-inter text-base text-gray-700 group-hover:text-gray-900">
                  {service}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Estimated Budget */}
        <div>
          <label htmlFor="budget" className="block font-inter text-sm font-medium text-gray-700 mb-2">
            Estimated Budget <span className="text-red-500">*</span>
          </label>
          <select
            id="budget"
            name="budget"
            required
            value={formData.budget}
            onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors cursor-pointer"
          >
            <option value="">Select a budget range</option>
            <option value="$1,500 – $2,500">$1,500 – $2,500</option>
            <option value="$2,500 – $5,000">$2,500 – $5,000</option>
            <option value="$5,000+">$5,000+</option>
            <option value="Not sure yet">Not sure yet</option>
          </select>
          <p className="font-inter text-xs text-gray-500 mt-2">
            Most projects start at $1,500.
          </p>
        </div>

        {/* When do you want to start? */}
        <div>
          <label htmlFor="timeline" className="block font-inter text-sm font-medium text-gray-700 mb-2">
            When do you want to start? <span className="text-red-500">*</span>
          </label>
          <select
            id="timeline"
            name="timeline"
            required
            value={formData.timeline}
            onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors cursor-pointer"
          >
            <option value="">Select a timeline</option>
            <option value="ASAP">ASAP</option>
            <option value="Within 2 weeks">Within 2 weeks</option>
            <option value="Within 30 days">Within 30 days</option>
            <option value="1–3 months">1–3 months</option>
            <option value="Just exploring">Just exploring</option>
          </select>
        </div>

        {/* Preferred Contact Method */}
        <div>
          <label className="block font-inter text-sm font-medium text-gray-700 mb-3">
            Preferred Contact Method <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-4">
            {['Email', 'Phone', 'Text'].map((method) => (
              <label key={method} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="contactMethod"
                  value={method}
                  required
                  checked={formData.contactMethod === method}
                  onChange={(e) => setFormData({ ...formData, contactMethod: e.target.value })}
                  className="w-5 h-5 border-2 border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                />
                <span className="font-inter text-base text-gray-700 group-hover:text-gray-900">
                  {method}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Project Details */}
        <div>
          <label htmlFor="projectDetails" className="block font-inter text-sm font-medium text-gray-700 mb-2">
            Project Details <span className="text-red-500">*</span>
          </label>
          <textarea
            id="projectDetails"
            name="projectDetails"
            required
            rows={6}
            maxLength={500}
            value={formData.projectDetails}
            onChange={(e) => setFormData({ ...formData, projectDetails: e.target.value })}
            placeholder="Tell me what you want built, any pages/features you need, and what success looks like for your business."
            className="w-full px-4 py-3 border border-gray-200 rounded-lg font-inter text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
          />
          <p className="font-inter text-xs text-gray-500 mt-2">
            {formData.projectDetails.length}/500 characters
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || formData.services.length === 0}
          className="w-full bg-[#D4AF37] hover:bg-[#C19B2E] text-white font-inter font-semibold text-base px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Sending...' : 'Request a Call'}
        </button>
      </form>

      {/* Secondary CTA */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="text-center">
          <h3 className="font-sora font-semibold text-xl text-charcoal mb-4">
            Prefer to schedule right now?
          </h3>
          <button
            onClick={() => {
              const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
              if (widget) widget.click();
            }}
            className="inline-block bg-charcoal hover:bg-charcoal/90 text-white font-inter font-semibold text-base px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            Book a Call
          </button>
          <p className="font-inter text-sm text-gray-500 mt-3">
            You can also connect with me directly through the AI assistant
          </p>
        </div>
      </div>
    </div>
  );
}
